import React, { useState, useEffect, useRef } from "react";
import "./CalendarWidget.scss";
import { useNavigate } from "react-router-dom";
import { WrittenFormat } from "../WrittenFormat";
import { formatPartyNames } from "../TruncateParties";
import taskdisplay from "../../assets/images/task-display-main.png";
import hearingicon from "../../assets/images/bar-icon.png";
import draftToHtml from "draftjs-to-html";
import { MdOutlineDescription } from "react-icons/md";
import { GoLaw } from "react-icons/go";

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const CalendarWidget = ({ data = {}, onDateSelect }) => {
  const navigate = useNavigate();
  const today = new Date();
  const [tooltipTimeout, setTooltipTimeout] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState("bottom");
  const [activeTab, setActiveTab] = useState("hearings");
  const tooltipRef = useRef(null);

  const [selectedDate, setSelectedDate] = useState(null);

  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
  const [calendarMatrix, setCalendarMatrix] = useState([]);
  const [hoveredDate, setHoveredDate] = useState(null);
  // const calendarRef = useRef(null);

  const years = Array.from(
    { length: 20 },
    (_, i) => today.getFullYear() - 10 + i
  );
  const months = [...Array(12).keys()];

  const getFormattedDate = (date) => {
    return date.toLocaleDateString("en-CA");
  };

  useEffect(() => {
    if (!tooltipRef.current || !hoveredDate) return;

    const tooltip = tooltipRef.current;
    const dayElement = tooltip.closest(".day");
    const dayRect = dayElement?.getBoundingClientRect();

    if (!dayRect) return;

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Tooltip dimensions
    const tooltipWidth = 380;
    const tooltipHeight = 350;
    const padding = 20;

    // Calculate available space in each direction
    const spaceAbove = dayRect.top - padding;
    const spaceBelow = viewportHeight - dayRect.bottom - padding;
    const spaceLeft = dayRect.left - padding;
    const spaceRight = viewportWidth - dayRect.right - padding;

    let bestPosition = "bottom";
    let adjustLeft = false;
    let adjustRight = false;

    // Choose best vertical position first
    if (spaceBelow >= tooltipHeight) {
      bestPosition = "bottom";
    } else if (spaceAbove >= tooltipHeight) {
      bestPosition = "top";
    } else if (spaceRight >= tooltipWidth) {
      bestPosition = "right";
    } else if (spaceLeft >= tooltipWidth) {
      bestPosition = "left";
    } else {
      // Choose position with most space
      const maxSpace = Math.max(spaceAbove, spaceBelow, spaceLeft, spaceRight);
      if (maxSpace === spaceBelow) bestPosition = "bottom";
      else if (maxSpace === spaceAbove) bestPosition = "top";
      else if (maxSpace === spaceRight) bestPosition = "right";
      else bestPosition = "left";
    }

    // For top/bottom positions, check horizontal overflow
    if (bestPosition === "top" || bestPosition === "bottom") {
      const tooltipLeft = dayRect.left + dayRect.width / 2 - tooltipWidth / 2;
      const tooltipRight = tooltipLeft + tooltipWidth;

      if (tooltipLeft < padding) {
        adjustLeft = true;
      } else if (tooltipRight > viewportWidth - padding) {
        adjustRight = true;
      }
    }

    setTooltipPosition(bestPosition);

    // Apply positioning immediately
    if (tooltip) {
      // Reset all positioning
      tooltip.style.top = "";
      tooltip.style.bottom = "";
      tooltip.style.left = "";
      tooltip.style.right = "";
      tooltip.style.transform = "";
      tooltip.style.margin = "";

      switch (bestPosition) {
        case "bottom":
          tooltip.style.top = "100%";
          tooltip.style.marginTop = "8px";

          if (adjustLeft) {
            tooltip.style.left = "0";
            tooltip.style.transform = `translateX(${padding - dayRect.left}px)`;
          } else if (adjustRight) {
            tooltip.style.right = "0";
            tooltip.style.transform = `translateX(${dayRect.right - viewportWidth + padding
              }px)`;
          } else {
            tooltip.style.left = "50%";
            tooltip.style.transform = "translateX(-50%)";
          }
          break;

        case "top":
          tooltip.style.bottom = "100%";
          tooltip.style.marginBottom = "8px";

          if (adjustLeft) {
            tooltip.style.left = "0";
            tooltip.style.transform = `translateX(${padding - dayRect.left}px)`;
          } else if (adjustRight) {
            tooltip.style.right = "0";
            tooltip.style.transform = `translateX(${dayRect.right - viewportWidth + padding
              }px)`;
          } else {
            tooltip.style.left = "50%";
            tooltip.style.transform = "translateX(-50%)";
          }
          break;

        case "right":
          tooltip.style.left = "100%";
          tooltip.style.top = "50%";
          tooltip.style.transform = "translateY(-50%)";
          tooltip.style.marginLeft = "8px";
          break;

        case "left":
          tooltip.style.right = "100%";
          tooltip.style.top = "50%";
          tooltip.style.transform = "translateY(-50%)";
          tooltip.style.marginRight = "8px";
          break;
      }
    }
  }, [hoveredDate, activeTab]);

  const generateCalendar = () => {
    // Get the first day of the month and its day of the week (0 = Sunday, 1 = Monday, etc.)
    const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1);
    const firstDayWeekday = firstDayOfMonth.getDay(); // 0-6 (Sunday-Saturday)

    // Get the number of days in the current month
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();

    // Get the number of days in the previous month
    const daysInPrevMonth = new Date(selectedYear, selectedMonth, 0).getDate();

    const matrix = [];
    let currentDay = 1;
    let nextMonthDay = 1;
    let weekCount = 0;

    // Calculate total cells needed
    const totalCellsNeeded = firstDayWeekday + daysInMonth;
    const weeksNeeded = Math.ceil(totalCellsNeeded / 7);

    // Generate the required number of weeks (usually 4-6)
    for (let week = 0; week < weeksNeeded; week++) {
      const weekRow = [];

      for (let day = 0; day < 7; day++) {
        if (week === 0 && day < firstDayWeekday) {
          // Previous month's trailing days
          const prevMonthDay = daysInPrevMonth - (firstDayWeekday - day - 1);
          const prevMonthDate = new Date(
            selectedYear,
            selectedMonth - 1,
            prevMonthDay
          );
          weekRow.push({ date: prevMonthDate, isCurrentMonth: false });
        } else if (currentDay <= daysInMonth) {
          // Current month's days
          const currentDate = new Date(selectedYear, selectedMonth, currentDay);
          weekRow.push({ date: currentDate, isCurrentMonth: true });
          currentDay++;
        } else {
          // Next month's leading days
          const nextMonthDate = new Date(
            selectedYear,
            selectedMonth + 1,
            nextMonthDay
          );
          weekRow.push({ date: nextMonthDate, isCurrentMonth: false });
          nextMonthDay++;
        }
      }

      matrix.push(weekRow);
      weekCount++;

      // Stop when we've included all days of the current month
      // and filled the current week
      if (currentDay > daysInMonth && weekCount >= 4) {
        break;
      }
    }

    setCalendarMatrix(matrix);
  };

  useEffect(() => {
    generateCalendar();
  }, [selectedMonth, selectedYear]);

  // Modify handleDateClick
  // const handleDateClick = (date) => {
  //   if (!date) return;
  //   const dateStr = getFormattedDate(date);
  //   setSelectedDate(dateStr);
  //   if (onDateSelect) {
  //     onDateSelect(dateStr);  // Pass to parent
  //   }
  // };
  // Modify handleDateClick
  const handleDateClick = (date) => {
    if (!date) return;
    const dateStr = getFormattedDate(date);

    // Check if the clicked date is already the selected date
    if (selectedDate === dateStr) {
      // Toggle selection off
      setSelectedDate(null); 
      if (onDateSelect) {
        onDateSelect(null); // Inform parent that selection is cleared
      }
    } else {
      // Select the new date
      setSelectedDate(dateStr);
      if (onDateSelect) {
        onDateSelect(dateStr);
      }
    }
  };

  const handleSeeMore = (date) => {
    const formattedDate = getFormattedDate(date);

    navigate(`/home/displayboard/extended-view`, {
      state: {
        fullData: data,
        selectedDate: formattedDate,
      },
    });
  };

  const isToday = (date) => {
    if (!date) return false;
    return date.toDateString() === today.toDateString();
  };

  const hasEvents = (date) => {
    if (!date) return false;
    const dateStr = getFormattedDate(date);
    const dayData = data[dateStr];
    const incompleteTasks = (dayData?.tasks || []).filter(task => !task.isCompleted);
    return (
      dayData && (dayData.hearings?.length > 0 || incompleteTasks.length > 0)
    );
  };

  const handleMouseEnter = (dateStr, dayData) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);
    setHoveredDate(dateStr);

    // Set default active tab based on available data
    if (dayData?.hearings?.length > 0 && dayData?.tasks?.length > 0) {
      setActiveTab("hearings");
    } else if (dayData?.hearings?.length > 0) {
      setActiveTab("hearings");
    } else if (dayData?.tasks?.length > 0) {
      setActiveTab("tasks");
    }
  };

  const handleMouseLeave = () => {
    // Don't set timeout here - let tooltip handle it
  };

  const handleTooltipMouseEnter = () => {
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout);
      setTooltipTimeout(null);
    }
  };

  const handleTooltipMouseLeave = () => {
    const timeout = setTimeout(() => {
      setHoveredDate(null);
    }, 300);
    setTooltipTimeout(timeout);
  };

  // Calculate monthly totals
  const getMonthlyTotals = (year, month) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let totalHearings = 0;
    let totalTasks = 0;

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = getFormattedDate(date);
      const dayData = data[dateStr];

      if (dayData) {
        totalHearings += dayData.hearings?.length || 0;
        totalTasks += (dayData.tasks || []).filter(task => !task.isCompleted).length;
      }
    }

    return { totalHearings, totalTasks };
  };

  // Get current month totals
  const getCurrentMonthTotals = () => {
    const currentTotals = getMonthlyTotals(selectedYear, selectedMonth);
    return { ...currentTotals, isFromPreviousMonth: false };
  };

  return (
    <div className="calendar-container" 
    
    // ref={calendarRef}
    >
      <div className="calendar-header">
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
        >
          {months.map((month) => (
            <option key={month} value={month}>
              {new Date(0, month).toLocaleString("default", { month: "long" })}
            </option>
          ))}
        </select>

        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      <div className="calendar-grid">
        {WEEK_DAYS.map((day) => (
          <div key={day} className="weekday">
            {day}
          </div>
        ))}

        {calendarMatrix.map((week, i) =>
          week.map((dayObj, j) => {
            const { date, isCurrentMonth } = dayObj;
            const dateStr = date ? getFormattedDate(date) : null;
            const dayData = dateStr ? data[dateStr] : null;
            const hearings = dayData?.hearings || [];
            const tasks = (dayData?.tasks || []).filter(task => !task.isCompleted);
            const showTooltip =
              hoveredDate === dateStr &&
              (hearings.length > 0 || tasks.length > 0);

            return (
              <div
                key={`${i}-${j}`}
                className={`day ${!date ? "empty" : ""} ${isToday(date) ? "today" : ""
                  } ${hasEvents(date) ? "has-events" : ""} ${!isCurrentMonth ? "other-month" : ""
                  } ${selectedDate === getFormattedDate(date) ? "selected-date" : ""
                  }`} // Add this class
                onMouseEnter={() => date && handleMouseEnter(dateStr, dayData)}
                onMouseLeave={handleMouseLeave}
                onClick={() => handleDateClick(date)} // Add this onClick
              >
                {date && (
                  <>
                    <div className="day-number">{date.getDate()}</div>

                    {hasEvents(date) && isCurrentMonth && (
                      <div className="event-count">
                        {hearings.length + tasks.length} item
                        {hearings.length + tasks.length > 1 ? "s" : ""}
                      </div>
                    )}

                    {showTooltip && isCurrentMonth && (
                      <div
                        ref={tooltipRef}
                        className={`tooltip tooltip-${tooltipPosition}`}
                        onMouseEnter={handleTooltipMouseEnter}
                        onMouseLeave={handleTooltipMouseLeave}
                      >
                        {/* Tab Navigation */}
                        {hearings.length > 0 && tasks.length > 0 && (
                          <div className="tooltip-tabs">
                            <button
                              className={`tab ${activeTab === "hearings" ? "active" : ""
                                }`}
                              onClick={() => setActiveTab("hearings")}
                            >
                              <span className="tab-icon">
                                <img
                                  src={hearingicon}
                                  alt="HearingIcon"
                                  className="icon-image"
                                  width="16"
                                  height="16"
                                />
                              </span>
                              Court References ({hearings.length})
                            </button>
                            <button
                              className={`tab ${activeTab === "tasks" ? "active" : ""
                                }`}
                              onClick={() => setActiveTab("tasks")}
                            >
                              <span className="tab-icon">📋</span>
                              Tasks ({tasks.length})
                            </button>
                          </div>
                        )}

                        {/* Content Area */}
                        <div className="tooltip-content">
                          {/* Hearings Tab Content */}
                          {((activeTab === "hearings" && hearings.length > 0) ||
                            (hearings.length > 0 && tasks.length === 0)) && (
                              <div className="tooltip-section">
                                {hearings.length > 0 && tasks.length === 0 && (
                                  <div className="tooltip-title">
                                    <span className="section-icon">
                                      <img
                                        src={hearingicon}
                                        alt="HearingIcon"
                                        className="icon-image"
                                        width="16"
                                        height="16"
                                      />
                                    </span>
                                    Court References ({hearings.length})
                                  </div>
                                )}
                                <div className="tooltip-items scrollable">
                                  {hearings.map((hearing, idx) => (
                                    <div
                                      key={idx}
                                      className="tooltip-item clickable"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(
                                          `/home/cases/casedetails/${hearing.courtRef}`
                                        );
                                      }}
                                    >
                                      <div className="item-header">
                                        <span className="court-ref">
                                          {hearing?.caseDate?.caseTypeAndNumber}
                                        </span>
                                        <span className="item-badge">
                                          {hearing.type}
                                        </span>
                                      </div>
                                      <div className="item-details">
                                        {formatPartyNames(
                                          hearing?.caseDate?.parties
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* Tasks Tab Content */}
                          {((activeTab === "tasks" && tasks.length > 0) ||
                            (tasks.length > 0 && hearings.length === 0)) && (
                              <div className="tooltip-section">
                                {tasks.length > 0 && hearings.length === 0 && (
                                  <div className="tooltip-title">
                                    <span className="section-icon">
                                      {" "}
                                      <img
                                        src={taskdisplay}
                                        alt="TaskDisplay"
                                        className="icon-image"
                                        width="16"
                                        height="16"
                                      />
                                    </span>
                                    Tasks ({tasks.length})
                                  </div>
                                )}
                                <div className="tooltip-items scrollable">
                                  {tasks.map((task, idx) => (
                                    <div
                                      key={idx}
                                      className="tooltip-item clickable task-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(
                                          `/home/tasks/viewtask/${task._id}`
                                        );
                                      }}
                                    >
                                      <div className="item-header">
                                        <span className="task-name">
                                          {task.taskName ||
                                            task.name ||
                                            "Untitled Task"}
                                        </span>
                                        <span className="item-badge task-badge">
                                          Task
                                        </span>
                                      </div>
                                      <div className="item-details">
                                        {task.description ? (
                                          (() => {
                                            let html = "";
                                            try {
                                              const raw =
                                                typeof task.description ===
                                                  "string"
                                                  ? JSON.parse(task.description)
                                                  : task.description;
                                              if (raw && raw.blocks) {
                                                html = draftToHtml(raw);
                                              } else {
                                                html = task.description;
                                              }
                                            } catch (e) {
                                              html = task.description;
                                            }
                                            // Fix anchor tags to always use absolute URLs
                                            let fixedHtml = html.replace(
                                              /<a ([^>]*href=["'])(?!https?:\/\/)([^"'>]+)(["'][^>]*)>/gi,
                                              "<a $1https://$2$3>"
                                            );

                                            fixedHtml = fixedHtml.replace(
                                              /<a /gi,
                                              '<a target="_blank" rel="noopener noreferrer" '
                                            );
                                            return (
                                              <div
                                                dangerouslySetInnerHTML={{
                                                  __html: fixedHtml,
                                                }}
                                              />
                                            );
                                          })()
                                        ) : (
                                          <p>No description provided</p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                        </div>

                        <button
                          className="see-more-btn"
                          onClick={() => handleSeeMore(date)}
                        >
                          <span></span>
                          View All Details
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Monthly Summary Section */}
      {(() => {
        const monthlyTotals = getCurrentMonthTotals();
        return (
          <div className="monthly-summary">
            <div className="summary-header">
              <h4>Monthly Summary</h4>
            </div>
            <div className="summary-stats">
              <div className="stat-item hearings-stat">
                <div className="stat-content">
                  <span className="stat-number">
                    {monthlyTotals.totalHearings}
                  </span>
                  <span className="stat-label">Total Hearings</span>
                </div>
              </div>

              <div className="stat-item tasks-stat">
                <div className="stat-content">
                  <span className="stat-number">
                    {monthlyTotals.totalTasks}
                  </span>
                  <span className="stat-label">Total Tasks</span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CalendarWidget;
